import React, { useEffect, useState } from 'react';
import { logEvent } from '../../analytics';
import { EditOutlined } from '@ant-design/icons';
import { EVENTS } from '../../constants/ga-constants';
import { Button, Card, Divider, notification, Space, Spin } from 'antd';
import AddressMap from './showMap';
import UpdateAddress from './updateAddress';
import axios from '../../axios';

const EditComponent = ({ handleClick }) => {
  return (
    <Button
      type="primary"
      icon={<EditOutlined />}
      onClick={handleClick}
      iconPosition="start"
      style={{ background: '#274178' }}>
      Edit
    </Button>
  );
};

export default function NewAddress() {
  const [address, setAddress] = useState(null);
  const [editRequest, setEditRequest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [coordinates, setCoordinates] = useState({
    lat: null,
    lng: null,
  });
  const [api, contextHolder] = notification.useNotification();

  const openNotificationWithIcon = (type, message, desc) => {
    api[type]({
      message: message,
      description: desc,
      placement: 'topRight',
    });
  };

  const getPersonalDetails = () => {
    setLoading(true);
    axios
      .get(`v1/user`)
      .then(res => {
        let data = res?.data?.data || {};
        // Defensive: handle null/undefined for all fields
        const safe = v => (v !== undefined && v !== null && v !== 'undefined' ? v : '');
        const formatted = {
          id: safe(data.id),
          name: safe(data.first_name),
          surname: safe(data.last_name),
          flat: safe(data.flat),
          apartment: safe(data.apartment),
          street: safe(data.street),
          mobileNo: safe(data.phone),
          alterMobileNo: safe(data.phone2),
          emailId: safe(data.email),
          state: safe(data.state_name),
          city: safe(data.city_name),
          locality: safe(data.locality),
          pincode: safe(data.zip),
          latitude: safe(data.latitude),
          longitude: safe(data.longitude),
        };
        setAddress(formatted);
        setCoordinates({
          lat: formatted.latitude || null,
          lng: formatted.longitude || null,
        });
        // If no lat/lng, force edit mode
        if (!formatted.latitude || !formatted.longitude) {
          setEditRequest(true);
        }
      })
      .catch(err => {
        setAddress(null);
        openNotificationWithIcon(
          'error',
          'Failed to load address',
          'Could not fetch your address details.'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getPersonalDetails();
    // eslint-disable-next-line
  }, []);

  const updateUserAddress = _newData => {
    setLoading(true);
    let newAddressData = {
      id: _newData.id,
      first_name: _newData.name,
      last_name: _newData.surname,
      phone: _newData.mobileNo,
      phone2: _newData.alterMobileNo,
      email: _newData.emailId,
      flat: _newData.flat,
      apartment: _newData.apartment,
      street: _newData.street,
      city_name: _newData.city,
      locality: _newData.locality,
      gmap_meta_data: _newData.gmap_meta_data,
      state_name: _newData.state,
      latitude: _newData.latitude,
      longitude: _newData.longitude,
    };

    axios
      .post(`v1/updateUserDetails`, newAddressData)
      .then(() => {
        getPersonalDetails();
        logEvent(EVENTS.UPDATED_ADDRESS.event_name);
        openNotificationWithIcon(
          'success',
          'Address Updated',
          'Your address has been updated successfully.'
        );
        setEditRequest(false);
      })
      .catch(() => {
        openNotificationWithIcon(
          'error',
          'Update Failed',
          'Could not update your address. Please try again.'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Helper to safely join address fields, skipping null/empty
  const renderAddressString = addr => {
    if (!addr) return '';
    return [addr.flat, addr.apartment, addr.street, addr.city, addr.state, addr?.pincode]
      .filter(v => v && v !== 'undefined')
      .join(' ');
  };

  return (
    <>
      {contextHolder}
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '80vh',
          }}>
          <div className="loading">
            <img src="./book-loader.gif" alt="Loading..." />
          </div>
        </div>
      ) : address ? (
        !editRequest ? (
          <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
            <Card
              title="Address"
              size="default"
              extra={<EditComponent handleClick={() => setEditRequest(true)} />}>
              <p>
                {renderAddressString(address) || (
                  <span style={{ color: '#888' }}>No address available</span>
                )}
              </p>
              <Divider />
              {address.latitude && address.longitude ? (
                <AddressMap mapSize={'100%'} coordinates={coordinates} />
              ) : (
                <span style={{ color: '#888' }}>No map location available</span>
              )}
            </Card>
          </Space>
        ) : (
          <UpdateAddress
            address={address}
            onEditClick={() => setEditRequest(false)}
            setCoordinates={setCoordinates}
            coordinates={coordinates}
            setAddress={setAddress}
            updateUserAddress={updateUserAddress}
            setLoading={setLoading}
          />
        )
      ) : (
        <div style={{ color: '#888', textAlign: 'center', marginTop: 32 }}>
          No address data found.
        </div>
      )}
    </>
  );
}
